import React, { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { MapFeatures } from './mapFeatures';
import { PropertyCard } from './property-card';

import { Cluster, Renderer } from '@googlemaps/markerclusterer';
import LuxuryCluster from '@assets/images/luxury-cluster.png';

import { ILuxuryHome, ILuxuryHomeLocation } from '@share/common-types';
import { Responsive, RootState } from '@share/utils';
import { Map } from '@utils';

import './style.scss';
import { UNDEFINED_VALUE } from '@constants';

const condoMarketIcon = {
  url: LuxuryCluster,
};

const zoom = 4;

class ClusterRenderer implements Renderer {
  render(cluster: Cluster): google.maps.Marker {
    return new google.maps.Marker({
      position: cluster.position,
      icon: {
        url: LuxuryCluster,
      },
      label: {
        text: String(cluster.count),
        color: '#006579',
        fontSize: '12px',
        fontWeight: 'bold',
        className: 'luxury-cluster-label',
      },
      title: '',
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count,
    });
  }
}

interface IPlaceLocation {
  location: ILuxuryHomeLocation;
  homeId: number;
  unitsCount: number;
}

const LuxuryHomesOurLocations: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);

  const { homes } = useSelector((state: RootState) => state.luxuryHomesStore);

  const [map, setMap] = useState<google.maps.Map | undefined>(undefined);
  const [selectedHome, setSelectedHome] = useState<ILuxuryHome | null>(null);
  const [places, setPlaces] = useState<IPlaceLocation[]>([]);
  const [visiblePlaces, setVisiblePlaces] = useState<IPlaceLocation[] | null>([]);

  const onDrag = () => {
    setSelectedHome(null);
  }

  const handleSelectHome = (propertId: number, selectedMarker?: google.maps.Marker): void => {
    const selected: ILuxuryHome = homes?.find((home: ILuxuryHome) => home.id === propertId);
      
    if (selected) {
      setSelectedHome(selected);
  
      const offSetFromBottom = 50;
      const divHeightOfTheMap = mapRef?.current?.clientHeight;
      const position: google.maps.LatLngLiteral = {
        lat: selectedMarker ? get(selectedMarker, 'getPosition().lat()', 0) : selected.location.location.lat,
        lng: selectedMarker ? get(selectedMarker, 'getPosition().lng()', 0) : selected.location.location.lng,
      };
      const currentZoom = map?.getZoom();

      map?.panTo(position);

      if ((currentZoom ? currentZoom : 0) < zoom) {
        map?.setZoom(zoom);
      }

      map?.panBy(0, - ((divHeightOfTheMap ? divHeightOfTheMap : 0) / 2 - offSetFromBottom));
    }
  };

  const setMarkers = (): void => {
    Map.removeAllMarkers();

    Map.addMarkersParallel(map, visiblePlaces?.map(place => ({
      location: {
        latitude: place?.location?.location?.lat,
        longitude: place?.location?.location?.lng      
      },
      label: {
        text: String(place?.location?.unitsCount || 1),
        color: '#006579',
        fontSize: '12px',
        fontWeight: 'bold',
        className: 'luxury-cluster-label',
      },
      id: Number(place?.homeId || 0),
      disableTooltip: true,
      availability: String('0')
    })), condoMarketIcon, null, selectedHome?.id ? Number(selectedHome?.id) : 0, handleSelectHome);
    
    Map.addCluster(map, new ClusterRenderer(), 2);
  };

  const onMapChange = () => {
    const bounds = map?.getBounds();
    const sw = bounds?.getSouthWest();
    const ne = bounds?.getNorthEast();
    const mapBounds = {
      northEast: {
        longitude: ne?.lng(),
        latitude: ne?.lat(),
      },
      southWest: {
        longitude: sw?.lng(),
        latitude: sw?.lat(),
      },
    };
    
    const boundsSouthWestLatitude = mapBounds.southWest?.latitude ? mapBounds.southWest?.latitude : 0;
    const boundsSouthWestLongitude = mapBounds.southWest?.longitude ? mapBounds.southWest?.longitude : 0;
    
    const boundsNorthEastLatitude = mapBounds.northEast?.latitude ? mapBounds.northEast?.latitude : 0;
    const boundsNorthEastLongitude = mapBounds.northEast?.longitude ? mapBounds.northEast?.longitude : 0;

    const currentZoom = map?.getZoom();

    const visiblePlaces = places?.filter(({ location }) => {
      const isSouthWestLongitudePositive = Math.sign(boundsSouthWestLongitude) >= 0;
      const isNorthEastLongitudeNegative = Math.sign(boundsNorthEastLongitude) === -1;

      const isScrollPassed = (isNorthEastLongitudeNegative && isSouthWestLongitudePositive) || (!isSouthWestLongitudePositive && isNorthEastLongitudeNegative && boundsNorthEastLongitude < boundsSouthWestLongitude);

      const Latitude = location.location?.lat;
      const Longitude = location.location?.lng;

      if (isScrollPassed) {
        return Latitude >= boundsSouthWestLatitude && Latitude <= boundsNorthEastLatitude && 
               ((Longitude >= boundsSouthWestLongitude && Longitude <= 180) ||
                (Longitude >= -180 && Longitude <= boundsNorthEastLongitude))
      }

      return Latitude >= boundsSouthWestLatitude && Latitude <= boundsNorthEastLatitude && Longitude >= boundsSouthWestLongitude && Longitude <= boundsNorthEastLongitude
    });

    setSelectedHome(null);
    setVisiblePlaces((!visiblePlaces?.length && (currentZoom ? currentZoom : 0) <= 2) ? places : visiblePlaces);
  }

  useEffect(() => {
    const locations = homes?.map((home: ILuxuryHome) => ({
      homeId: home?.id,
      location: home?.location
    }));

    
    setPlaces(locations);
    setVisiblePlaces(locations);
  }, [homes]);

  useEffect(() => {
    if (map) {
      setMarkers();
    }
  }, [map]);

  useEffect(() => {
    setMarkers();
  }, [visiblePlaces]);

  useEffect(() => {
    if (mapRef.current && !map && places?.length) {
      const mapInstance = Map.initializeMap(mapRef?.current, { lat: 0, lng: 0 }, { 
        disableDefaultUI: true,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        zoomControl: true,
        maxZoom: 17,
        restriction: {
          latLngBounds: {north: 89, south: -89, west: -180, east: 180},
          strictBounds: true
        },
        styles: [...(MapFeatures as any[])]
      }, 2);

      mapInstance.addListener('drag', onDrag);
      mapInstance.addListener('zoom_changed', onMapChange);
      mapInstance.addListener('dragend', onMapChange);
      mapInstance.addListener('click', () => {
        if (selectedHome) {
          setSelectedHome(null);
        }
      });

      setMap(mapInstance);
    }
  }, [mapRef.current, places?.length, map]);

  return (
    <section className="our-locations">
      <div className="our-locations__header">
        <h2>OUR LOCATIONS</h2>
        <h3>Exclusive Destinations Around the World</h3>
        <p>From vibrant cities to serene coastal retreats. our handpicked homes span some of the most sought-after destinations. Use the interactive map below to explore where your next extraordinary stay awaits.</p>
      </div>
      <div className="our-locations__grid">
        <div className="our-locations__map-instance" ref={mapRef} />

        {selectedHome && (
          <div className="our-locations__home-detail">
            <Responsive>
              <PropertyCard property={selectedHome} onClick={() => {
                setSelectedHome(null);
              }} />
            </Responsive>
          </div>)}

      </div>
    </section>
  );
};

export default LuxuryHomesOurLocations; 